import dayjs, {Dayjs} from "dayjs";
import {RaccoltaCalendarEvent, RaccoltaResponse, SelectValue} from "../types";
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {SEZIONE_AVIS} from "./config";

dayjs.extend(customParseFormat);

export const PHONE_PREFIXES: Array<SelectValue> = [
  {value: '+39', name: '+39'},
  {value: '+378', name: '+378'},
  {value: '+41', name: '+41'},
  {value: '+33', name: '+33'},
  {value: '+49', name: '+49'},
  {value: '+34', name: '+34'},
  {value: '+44', name: '+44'},
  {value: '+43', name: '+43'},
  {value: '+32', name: '+32'},
  {value: '+31', name: '+31'},
  {value: '+351', name: '+351'},
  {value: '+30', name: '+30'},
  {value: '+40', name: '+40'},
  {value: '+48', name: '+48'},
  {value: '+355', name: '+355'},
  {value: '+380', name: '+380'},
  {value: '+212', name: '+212'},
  {value: '+216', name: '+216'},
  {value: '+20', name: '+20'},
  {value: '+1', name: '+1'},
  {value: '+7', name: '+7'},
  {value: '+86', name: '+86'},
  {value: '+91', name: '+91'},
  {value: '+55', name: '+55'},
];

export const DEFAULT_PHONE_PREFIX = '+39';

// Splits a stored phone (which may already include a prefix, e.g. "+393331234567")
// into its prefix and the remaining national number.
export const splitPhoneNumber = (full?: string): {prefix: string; number: string} => {
  const trimmed = (full ?? '').trim();
  const match = PHONE_PREFIXES
    .map((p) => p.value)
    .sort((a, b) => b.length - a.length)
    .find((p) => trimmed.startsWith(p));
  if (match) {
    return {prefix: match, number: trimmed.slice(match.length).trim()};
  }
  return {prefix: DEFAULT_PHONE_PREFIX, number: trimmed};
};

export const validatePhoneNumber = (phoneNumber: string): boolean => {
  const phoneRegex = /^\+?[0-9\s\-()]{7,12}$/;
  const digitsOnly = phoneNumber.replace(/[\s\-()]/g, '');
  const digitsCount = digitsOnly.startsWith('+') ? digitsOnly.length - 1 : digitsOnly.length;
  return phoneRegex.test(phoneNumber) && digitsCount >= 7 && digitsCount <= 15;
};

export const validateTaxCode = (taxCode: string): boolean => {
  const taxCodeRegex = /^[A-Z]{6}[0-9]{2}[A-Z]{1}[0-9]{2}[A-Z]{1}[0-9]{3}[A-Z]{1}$/;

  return taxCodeRegex.test(taxCode);
};

export const scale = (size: number) => {
  const BASE_DESKTOP = 1080;
  const desktop_factor = size / BASE_DESKTOP;
  const BASE_MOBILE = 600;
  const mobileFactor = size / BASE_MOBILE;
  let isMobile = false;

  if (typeof window !== 'undefined') {
    isMobile = window.innerWidth <= 600;
  }
  if (isMobile) {
    return `${mobileFactor * 100}vw`;
  } else {
    return `calc(${desktop_factor * 100}vmin)`;
  }
};

export const timeStringToDayJs = (time: string) => {
  return dayjs(time, "HH:mm");
}

export const formatDateInfo = (input: Date | string | Dayjs) => {
  const d = dayjs(input).locale('it');

  return {
    weekday: d.format('dddd'),
    day: d.date(),
    month: d.format('MMMM'),
    year: d.year(),
  };
};

export const parseRaccoltaCalendarEvents = (events: RaccoltaCalendarEvent[]): RaccoltaCalendarEvent[] => {
  return events.map((r) => ({
    ...r,
    start: new Date(r.start),
    end: new Date(r.end),
  }));
}

export const formatTime = (time: string): string => {
  // "07:30:00" -> "07:30"
  return time.split(':').slice(0, 2).join(':');
};

export const addMinutesToTimeString = (time: string, minutesToAdd: number): string => {
  const [hoursStr, minutesStr] = time.split(':');
  let hours = parseInt(hoursStr, 10);
  let minutes = parseInt(minutesStr, 10);

  minutes += minutesToAdd;
  if (minutes >= 60) {
    hours += Math.floor(minutes / 60);
    minutes = minutes % 60;
  }
  if (hours >= 24) {
    hours = hours % 24;
  }

  const hh = hours.toString().padStart(2, '0');
  const mm = minutes.toString().padStart(2, '0');

  return `${hh}:${mm}:00`;
};

export const getFasciaRange = (raccolta: RaccoltaResponse, index: number): string => {
  const fascia = raccolta.fasceETurni?.[index]?.fascia;
  if (!fascia) return '';
  return `${formatTime(fascia.start)} - ${formatTime(fascia.end)}`;
};

export const getFasciaAmPmById = (raccolta: RaccoltaResponse, fasciaId: number): number | null => {
  const fasciaETurni = raccolta.fasceETurni.find(el => el.fascia.id === fasciaId)

  if (!fasciaETurni) return null;
  return fasciaETurni.fascia.amPm
}

export const getTurnoHourById = (raccolta: RaccoltaResponse, turnoId: number): string => {
  let turnoHour = ''
  raccolta.fasceETurni.forEach(el => {
    el.turni.forEach(turno => {
      if (turno.id === turnoId) {
        turnoHour = formatTime(turno.start)
      }
    })
  })
  return turnoHour;
}

export const mapEventType = (type: string | number): 'sangue' | 'plasma' | 'plasma & sangue' => {
  if (type === 0 || type === '0') return 'sangue';
  if (type === 1 || type === '1') return 'plasma';
  if (type === 'plasma & sangue') return 'plasma & sangue';
  if (type === 'sangue') return 'sangue';
  if (type === 'plasma') return 'plasma';
  return 'sangue';
};

export function buildPath(path: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `/${SEZIONE_AVIS}${normalizedPath}`;
}

export const isEventPast = (eventDate: Date) => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return eventDate <= yesterday;
};



